import { memo, useState } from 'react';
import type { CatalogFacets } from '@sbg/shared/schemas';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { CheckboxField } from '@/components/ui/Checkbox';
import { Slider } from '@/components/ui/Slider';
import { SwitchField } from '@/components/ui/Switch';
import { toggleFilterValue, toggleThickness } from '@/lib/catalog';
import type { CatalogFilterState, ListFilterKey } from '@/lib/catalog';
import { pricesShown } from '@/lib/catalog/storefront';
import { useFormatters, useT } from '@/lib/i18n';
import type { ShopCopy } from './content';
import {
  SLIDER_STEPS,
  availabilityOptions,
  categoryOptions,
  positionToPrice,
  priceBounds,
  priceToPosition,
  refOptions,
  thicknessOptions,
} from './shop-utils';
import type { FacetOption } from './shop-utils';
import type { UpdateMode } from './useShopFilters';

export interface FilterPanelProps {
  facets: CatalogFacets | undefined;
  filters: CatalogFilterState;
  onChange: (next: CatalogFilterState, mode?: UpdateMode) => void;
  copy: ShopCopy['filters'];
  /** Distinguishes element ids when the panel is rendered twice (sidebar + drawer). */
  idPrefix: string;
}

const OPEN_BY_DEFAULT = ['category', 'color', 'finish', 'application', 'price'];

/** One toggle button per option, for short lists where a swatch or a chip reads better than a row. */
function ChipOption({ option, onToggle }: { option: FacetOption; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="filter-chip"
      aria-pressed={option.selected}
      disabled={option.count === 0 && !option.selected}
      onClick={onToggle}
    >
      {option.swatchHex ? (
        <span className="filter-chip__swatch" style={{ backgroundColor: option.swatchHex }} />
      ) : null}
      <span>{option.label}</span>
      <span className="filter-chip__count">{option.count}</span>
    </button>
  );
}

/**
 * The filter groups — category, colour family, finish, origin, application, thickness, price,
 * availability, featured. Counts come from the facets endpoint (they already account for the other
 * active filters, and a facet never zeroes itself out); options with nothing behind them are
 * disabled. Used in the sidebar and, identically, in the mobile drawer.
 */
export const FilterPanel = memo(function FilterPanel({
  facets,
  filters,
  onChange,
  copy,
  idPrefix,
}: FilterPanelProps) {
  const t = useT();
  const { formatMoney } = useFormatters();
  const bounds = priceBounds(facets);

  const toggleList = (key: ListFilterKey, value: string): void => {
    onChange(toggleFilterValue(filters, key, value));
  };

  const listGroup = (key: 'finish' | 'origin' | 'application') =>
    refOptions(facets, filters, key).map((option) => (
      <CheckboxField
        key={option.value}
        id={`${idPrefix}-${key}-${option.value}`}
        label={option.label}
        count={option.count}
        checked={option.selected}
        disabled={option.count === 0 && !option.selected}
        onCheckedChange={() => {
          toggleList(key, option.value);
        }}
      />
    ));

  const selectedCount = {
    category: filters.category ? 1 : 0,
    color: filters.color?.length ?? 0,
    finish: filters.finish?.length ?? 0,
    origin: filters.origin?.length ?? 0,
    application: filters.application?.length ?? 0,
    thickness: filters.thickness?.length ?? 0,
    price: filters.priceMin !== undefined || filters.priceMax !== undefined ? 1 : 0,
    availability: filters.availability?.length ?? 0,
  };

  // The slider shows its own value while dragging and writes to the URL on release.
  const committed: [number, number] | null = bounds
    ? [
        priceToPosition(filters.priceMin ?? bounds.min, bounds.min, bounds.max),
        priceToPosition(filters.priceMax ?? bounds.max, bounds.min, bounds.max),
      ]
    : null;
  const [dragging, setDragging] = useState<number[] | null>(null);
  const position = dragging ?? committed;
  const priceAt = (pos: number): number =>
    bounds ? positionToPrice(pos, bounds.min, bounds.max) : 0;
  const money = (rupees: number): string => formatMoney(rupees * 100);

  const commitPrice = (next: number[]): void => {
    setDragging(null);
    if (!bounds) return;
    const [low = 0, high = SLIDER_STEPS] = next;
    const { priceMin: _min, priceMax: _max, ...rest } = filters;
    const min = priceAt(low);
    const max = priceAt(high);
    onChange(
      {
        ...rest,
        ...(low > 0 ? { priceMin: min } : {}),
        ...(high < SLIDER_STEPS ? { priceMax: max } : {}),
      },
      'replace',
    );
  };

  // The filters sit under the page's <h1> (or the drawer's <h2> title), so their rows are <h2>.
  const group = (id: keyof typeof selectedCount, title: string) => (
    <AccordionTrigger headingLevel={2}>
      {title}
      {selectedCount[id] > 0 ? (
        <span className="filter-group__badge">{selectedCount[id]}</span>
      ) : null}
    </AccordionTrigger>
  );

  const categories = categoryOptions(facets, filters);
  const thicknesses = thicknessOptions(facets, filters);
  const availabilities = availabilityOptions(facets, filters);

  return (
    <div className="filter-panel" data-loading={facets ? undefined : 'true'}>
      <Accordion type="multiple" defaultValue={OPEN_BY_DEFAULT}>
        {categories.length > 0 ? (
          <AccordionItem value="category">
            {group('category', copy.category)}
            <AccordionContent>
              <div className="filter-chips">
                <button
                  type="button"
                  className="filter-chip"
                  aria-pressed={!filters.category}
                  onClick={() => {
                    const { category: _removed, ...rest } = filters;
                    onChange(rest);
                  }}
                >
                  {copy.allCategories}
                </button>
                {categories.map((option) => (
                  <ChipOption
                    key={option.value}
                    option={option}
                    onToggle={() => {
                      const { category: _removed, ...rest } = filters;
                      onChange(option.selected ? rest : { ...rest, category: option.value });
                    }}
                  />
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ) : null}

        <AccordionItem value="color">
          {group('color', copy.color)}
          <AccordionContent>
            <div className="filter-chips">
              {refOptions(facets, filters, 'color').map((option) => (
                <ChipOption
                  key={option.value}
                  option={option}
                  onToggle={() => {
                    toggleList('color', option.value);
                  }}
                />
              ))}
            </div>
          </AccordionContent>
        </AccordionItem>

        <AccordionItem value="finish">
          {group('finish', copy.finish)}
          <AccordionContent>{listGroup('finish')}</AccordionContent>
        </AccordionItem>

        <AccordionItem value="application">
          {group('application', copy.application)}
          <AccordionContent>{listGroup('application')}</AccordionContent>
        </AccordionItem>

        <AccordionItem value="origin">
          {group('origin', copy.origin)}
          <AccordionContent>{listGroup('origin')}</AccordionContent>
        </AccordionItem>

        {thicknesses.length > 0 ? (
          <AccordionItem value="thickness">
            {group('thickness', copy.thickness)}
            <AccordionContent>
              <div className="filter-chips">
                {thicknesses.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    className="filter-chip"
                    aria-pressed={option.selected}
                    disabled={option.count === 0 && !option.selected}
                    onClick={() => {
                      onChange(toggleThickness(filters, option.value));
                    }}
                  >
                    <span>{t('product.thickness', { mm: option.value })}</span>
                    <span className="filter-chip__count">{option.count}</span>
                  </button>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ) : null}

        {pricesShown() && bounds && position ? (
          <AccordionItem value="price">
            {group('price', copy.price)}
            <AccordionContent>
              <Slider
                value={position}
                min={0}
                max={SLIDER_STEPS}
                step={1}
                minStepsBetweenThumbs={10}
                thumbLabels={[copy.minPrice, copy.maxPrice]}
                valueText={(pos) => money(priceAt(pos))}
                onValueChange={setDragging}
                onValueCommit={commitPrice}
              />
              <div className="filter-price">
                <span>{money(priceAt(position[0] ?? 0))}</span>
                <span>{money(priceAt(position[1] ?? SLIDER_STEPS))}</span>
              </div>
              <p className="filter-note">{copy.priceNote}</p>
            </AccordionContent>
          </AccordionItem>
        ) : null}

        {availabilities.length > 0 ? (
          <AccordionItem value="availability">
            {group('availability', copy.availability)}
            <AccordionContent>
              {availabilities.map((option) => (
                <CheckboxField
                  key={option.value}
                  id={`${idPrefix}-availability-${option.value}`}
                  label={t(`availability.${option.value}`)}
                  count={option.count}
                  checked={option.selected}
                  disabled={option.count === 0 && !option.selected}
                  onCheckedChange={() => {
                    toggleList('availability', option.value);
                  }}
                />
              ))}
            </AccordionContent>
          </AccordionItem>
        ) : null}
      </Accordion>

      <div className="filter-panel__extra">
        <SwitchField
          label={copy.featured}
          checked={filters.featured === true}
          onCheckedChange={(checked) => {
            const { featured: _removed, ...rest } = filters;
            onChange(checked ? { ...rest, featured: true } : rest);
          }}
        />
      </div>
    </div>
  );
});
